import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Boxes, Plus, Search, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useAppFeaturesQuery,
  useCreateAppFeatureMutation,
} from '@/client/api/feature-flags/app-features.query';
import { featureFlagPermissionError } from './utils';

const isActive = (v: unknown) => v === 1 || v === true || v === '1';

// ─── Create dialog ─────────────────────────────────────────────────────────

const CreateFeatureDialog = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const [form, setForm] = useState({
    code: '',
    name: '',
    description: '',
    etid: '',
    icon_url: '',
    order_by: '0',
  });
  const [error, setError] = useState<string | null>(null);
  const createMutation = useCreateAppFeatureMutation();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const code = form.code.trim();
    const name = form.name.trim();
    if (!code || !name) {
      setError('Code and name are required.');
      return;
    }
    try {
      await createMutation.mutateAsync({
        code,
        name,
        description: form.description.trim() || undefined,
        etid: form.etid ? Number(form.etid) : undefined,
        icon_url: form.icon_url.trim() || undefined,
        order_by: form.order_by ? Number(form.order_by) : 0,
      });
      toast.success(`Feature “${code}” created`);
      onClose();
    } catch (err: any) {
      toast.error(featureFlagPermissionError(err) ?? err?.message ?? 'Failed to create the feature.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Boxes className="h-5 w-5 text-indigo-600" />
            New app feature
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 py-2">
          <div className="space-y-1">
            <Label htmlFor="af-code">
              Code <span className="text-red-500">*</span>
            </Label>
            <Input
              id="af-code"
              placeholder="e.g. instant_book"
              value={form.code}
              onChange={(e) => setForm((p) => ({ ...p, code: e.target.value }))}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="af-name">
              Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="af-name"
              placeholder="e.g. Instant Book"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="af-desc">Description</Label>
            <Input
              id="af-desc"
              placeholder="What this feature offers"
              value={form.description}
              onChange={(e) =>
                setForm((p) => ({ ...p, description: e.target.value }))
              }
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="af-etid">Vertical (etid)</Label>
              <Input
                id="af-etid"
                inputMode="numeric"
                placeholder="optional"
                value={form.etid}
                onChange={(e) => setForm((p) => ({ ...p, etid: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="af-order">Order</Label>
              <Input
                id="af-order"
                inputMode="numeric"
                value={form.order_by}
                onChange={(e) =>
                  setForm((p) => ({ ...p, order_by: e.target.value }))
                }
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="af-icon">Icon URL</Label>
            <Input
              id="af-icon"
              placeholder="https://…"
              value={form.icon_url}
              onChange={(e) => setForm((p) => ({ ...p, icon_url: e.target.value }))}
            />
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Creating…' : 'Create feature'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ─── Panel ─────────────────────────────────────────────────────────────────

const AppFeaturesPanel: React.FC<{ canManage: boolean }> = ({ canManage }) => {
  const [activeOnly, setActiveOnly] = useState(true);
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  const { data: features = [], isLoading, isError, refetch } =
    useAppFeaturesQuery(activeOnly);

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return features;
    return features.filter(
      (f) =>
        f.name.toLowerCase().includes(term) ||
        f.code.toLowerCase().includes(term) ||
        (f.description ?? '').toLowerCase().includes(term)
    );
  }, [features, search]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            className="pl-9"
            placeholder="Search features by name, code or description…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-3">
          <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300"
              checked={activeOnly}
              onChange={(e) => setActiveOnly(e.target.checked)}
            />
            Active only
          </label>
          {canManage && (
            <Button size="sm" className="gap-2" onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" />
              New feature
            </Button>
          )}
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Feature', 'Code', 'Vertical', 'Order', 'State'].map((h) => (
                  <th
                    key={h}
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {isLoading &&
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 5 }).map((__, j) => (
                      <td key={j} className="px-6 py-4">
                        <Skeleton className="h-4 w-full" />
                      </td>
                    ))}
                  </tr>
                ))}

              {!isLoading && isError && (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-sm text-red-600">
                    Could not load app features.{' '}
                    <button
                      className="font-medium underline"
                      onClick={() => refetch()}
                    >
                      Retry
                    </button>
                  </td>
                </tr>
              )}

              {!isLoading && !isError && rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-sm text-gray-500">
                    No app features found.
                  </td>
                </tr>
              )}

              {!isLoading &&
                !isError &&
                rows.map((f) => (
                  <tr key={f.code} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {f.icon_url ? (
                          <img
                            src={f.icon_url}
                            alt=""
                            className="h-8 w-8 rounded object-cover"
                          />
                        ) : (
                          <div className="flex h-8 w-8 items-center justify-center rounded bg-indigo-50">
                            <Boxes className="h-4 w-4 text-indigo-500" />
                          </div>
                        )}
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {f.name}
                          </div>
                          {f.description && (
                            <div className="max-w-xs truncate text-xs text-gray-500">
                              {f.description}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <code className="rounded bg-gray-100 px-2 py-1 font-mono text-xs text-gray-800">
                        {f.code}
                      </code>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">
                      {f.vertical?.name_friendly ??
                        (f.etid != null ? `etid ${f.etid}` : '—')}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">
                      {f.order_by ?? 0}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          isActive(f.active)
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {isActive(f.active) ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        {!isLoading && !isError && (
          <p className="flex items-center gap-1.5 border-t border-gray-200 px-6 py-3 text-xs text-gray-500">
            <ExternalLink className="h-3.5 w-3.5" />
            {rows.length} feature{rows.length === 1 ? '' : 's'} from{' '}
            <code className="rounded bg-gray-100 px-1 py-0.5">/api/admin/app_features</code>
          </p>
        )}
      </div>

      {createOpen && (
        <CreateFeatureDialog open={createOpen} onClose={() => setCreateOpen(false)} />
      )}
    </div>
  );
};

export default AppFeaturesPanel;
